
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit2, Trash2, Tag, Hash } from 'lucide-react';
import { useInventaire } from '@/hooks/useInventaire';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

const iconsOptions = [
  { value: 'utensils', label: '🍴 Utensils' },
  { value: 'bottle', label: '🍼 Bottle' },
  { value: 'shirt', label: '👕 Shirt' },
  { value: 'soap', label: '🧼 Soap' },
  { value: 'fuel', label: '⛽ Fuel' },
  { value: 'wrench', label: '🔧 Wrench' },
  { value: 'package', label: '📦 Package' },
  { value: 'droplets', label: '💧 Droplets' },
  { value: 'sparkles', label: '✨ Sparkles' },
  { value: 'archive', label: '📋 Archive' },
];

const GestionCategories = () => {
  const { categories, sousCategories, fetchCategories, fetchSousCategories } = useInventaire();
  const { user } = useAuth();
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [isEditCategoryOpen, setIsEditCategoryOpen] = useState(false);
  const [isAddSubCategoryOpen, setIsAddSubCategoryOpen] = useState(false);
  const [isEditSubCategoryOpen, setIsEditSubCategoryOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<any>(null);
  const [categoryForm, setCategoryForm] = useState({ nom: '', icone: 'package' });
  const [subCategoryForm, setSubCategoryForm] = useState({ nom: '', categorie_id: '' });

  const handleAddCategory = async () => {
    if (!user) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour ajouter une catégorie.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('categories')
        .insert({ 
          nom: categoryForm.nom, 
          icone: categoryForm.icone,
          user_id: user.id
        });

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible d'ajouter la catégorie.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Succès",
          description: "Catégorie ajoutée avec succès !",
        });
        setCategoryForm({ nom: '', icone: 'package' });
        fetchCategories();
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const handleEditCategory = async () => {
    if (!selectedCategory) return;

    try {
      const { error } = await supabase
        .from('categories')
        .update({ nom: categoryForm.nom, icone: categoryForm.icone })
        .eq('id', selectedCategory.id);

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de modifier la catégorie.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Succès",
          description: "Catégorie modifiée avec succès !",
        });
        setCategoryForm({ nom: '', icone: 'package' });
        setSelectedCategory(null);
        fetchCategories();
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId);

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de supprimer la catégorie.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Succès",
          description: "Catégorie supprimée avec succès !",
        });
        fetchCategories();
        fetchSousCategories();
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const handleAddSubCategory = async () => {
    if (!user) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour ajouter une sous-catégorie.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('sous_categories')
        .insert({ 
          nom: subCategoryForm.nom, 
          categorie_id: subCategoryForm.categorie_id,
          user_id: user.id
        });

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible d'ajouter la sous-catégorie.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Succès",
          description: "Sous-catégorie ajoutée avec succès !",
        });
        setSubCategoryForm({ nom: '', categorie_id: '' });
        fetchSousCategories();
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const handleEditSubCategory = async () => {
    if (!selectedSubCategory) return;

    try {
      const { error } = await supabase
        .from('sous_categories')
        .update({ nom: subCategoryForm.nom, categorie_id: subCategoryForm.categorie_id })
        .eq('id', selectedSubCategory.id);

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de modifier la sous-catégorie.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Succès",
          description: "Sous-catégorie modifiée avec succès !",
        });
        setSubCategoryForm({ nom: '', categorie_id: '' });
        setSelectedSubCategory(null);
        fetchSousCategories();
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const handleDeleteSubCategory = async (subCategoryId: string) => {
    try {
      const { error } = await supabase
        .from('sous_categories')
        .delete()
        .eq('id', subCategoryId);

      if (error) {
        toast({
          title: "Erreur",
          description: "Impossible de supprimer la sous-catégorie.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Succès",
          description: "Sous-catégorie supprimée avec succès !",
        });
        fetchSousCategories();
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const openEditCategory = (category: any) => {
    setSelectedCategory(category);
    setCategoryForm({ nom: category.nom, icone: category.icone || 'package' });
    setIsEditCategoryOpen(true);
  };

  const openEditSubCategory = (subCategory: any) => {
    setSelectedSubCategory(subCategory);
    setSubCategoryForm({ nom: subCategory.nom, categorie_id: subCategory.categorie_id });
    setIsEditSubCategoryOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Gestion des catégories */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-nautic" />
              Catégories ({categories.length})
            </CardTitle>
            <Dialog open={isAddCategoryOpen} onOpenChange={setIsAddCategoryOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-nautic-gold text-nautic hover:bg-nautic-lightgold">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajouter une catégorie</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="nom">Nom de la catégorie</Label>
                    <Input
                      id="nom"
                      value={categoryForm.nom}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, nom: e.target.value }))}
                      placeholder="Ex: Alimentation"
                    />
                  </div>
                  <div>
                    <Label htmlFor="icone">Icône</Label>
                    <Select
                      value={categoryForm.icone}
                      onValueChange={(value) => setCategoryForm(prev => ({ ...prev, icone: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir une icône" />
                      </SelectTrigger>
                      <SelectContent>
                        {iconsOptions.map((icon) => (
                          <SelectItem key={icon.value} value={icon.value}>
                            {icon.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddCategory} className="flex-1">
                      Ajouter
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsAddCategoryOpen(false);
                        setCategoryForm({ nom: '', icone: 'package' });
                      }}
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2">
            {categories.map((category) => (
              <div key={category.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Badge variant="outline">{category.nom}</Badge>
                  {category.icone && (
                    <span className="text-sm text-muted-foreground">({category.icone})</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditCategory(category)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteCategory(category.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dialog de modification de catégorie */}
      <Dialog open={isEditCategoryOpen} onOpenChange={setIsEditCategoryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la catégorie</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-nom">Nom de la catégorie</Label>
              <Input
                id="edit-nom"
                value={categoryForm.nom}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, nom: e.target.value }))}
                placeholder="Ex: Alimentation"
              />
            </div>
            <div>
              <Label htmlFor="edit-icone">Icône</Label>
              <Select
                value={categoryForm.icone}
                onValueChange={(value) => setCategoryForm(prev => ({ ...prev, icone: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une icône" />
                </SelectTrigger>
                <SelectContent>
                  {iconsOptions.map((icon) => (
                    <SelectItem key={icon.value} value={icon.value}>
                      {icon.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleEditCategory} className="flex-1">
                Modifier
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditCategoryOpen(false);
                  setCategoryForm({ nom: '', icone: 'package' });
                  setSelectedCategory(null);
                }}
                className="flex-1"
              >
                Annuler
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gestion des sous-catégories */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5 text-nautic" />
              Sous-catégories ({sousCategories.length})
            </CardTitle>
            <Dialog open={isAddSubCategoryOpen} onOpenChange={setIsAddSubCategoryOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-nautic-gold text-nautic hover:bg-nautic-lightgold">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajouter une sous-catégorie</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="sub-categorie">Catégorie parent</Label>
                    <Select
                      value={subCategoryForm.categorie_id}
                      onValueChange={(value) => setSubCategoryForm(prev => ({ ...prev, categorie_id: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir une catégorie" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.nom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="sub-nom">Nom de la sous-catégorie</Label>
                    <Input
                      id="sub-nom"
                      value={subCategoryForm.nom}
                      onChange={(e) => setSubCategoryForm(prev => ({ ...prev, nom: e.target.value }))}
                      placeholder="Ex: Fruits frais"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddSubCategory} className="flex-1">
                      Ajouter
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsAddSubCategoryOpen(false);
                        setSubCategoryForm({ nom: '', categorie_id: '' });
                      }}
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2">
            {sousCategories.map((subCategory) => {
              const parentCategory = categories.find(c => c.id === subCategory.categorie_id);
              return (
                <div key={subCategory.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{subCategory.nom}</Badge>
                    <span className="text-sm text-muted-foreground">
                      → {parentCategory?.nom || 'Catégorie inconnue'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditSubCategory(subCategory)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteSubCategory(subCategory.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Dialog de modification de sous-catégorie */}
      <Dialog open={isEditSubCategoryOpen} onOpenChange={setIsEditSubCategoryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la sous-catégorie</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-sub-categorie">Catégorie parent</Label>
              <Select
                value={subCategoryForm.categorie_id}
                onValueChange={(value) => setSubCategoryForm(prev => ({ ...prev, categorie_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-sub-nom">Nom de la sous-catégorie</Label>
              <Input
                id="edit-sub-nom"
                value={subCategoryForm.nom}
                onChange={(e) => setSubCategoryForm(prev => ({ ...prev, nom: e.target.value }))}
                placeholder="Ex: Fruits frais"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleEditSubCategory} className="flex-1">
                Modifier
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditSubCategoryOpen(false);
                  setSubCategoryForm({ nom: '', categorie_id: '' });
                  setSelectedSubCategory(null);
                }}
                className="flex-1"
              >
                Annuler
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionCategories;
